import { Component } from '@ribajs/core'
import { getViewportDimensions } from '@ribajs/utils'
import {
  setShopifyImageSize,
  getTextColorForBackground,
  getButtonVariantForBackground,
  handleizeColor,
} from '../utils'

interface SbHeroComponentContent {
  mobileImageSrc?: string
  desktopImageSrc?: string
  bg?: string
  textColor?: string
  preheading?: string
  heading?: string
}

export interface SrcSetDefinition {
  url: string
  width?: number
  density?: number
}

/**
 * The types for the scope
 */
interface Scope {
  // OPTIONS
  showPosterOnVideoEnd: boolean
  posterImageUrl?: string
  posterImageMobileUrl?: string
  textColor?: string
  bgColor?: string
  buttonColor?: string

  mobileImageSrc: string
  desktopImageSrc: string

  // METHODS
  replaceContent: SbHeroComponent['replaceContent']
  onImageLoaded: SbHeroComponent['onImageLoaded']

  // TEMPLATES

  // ELEMENTS
  preheadingEl?: HTMLElement
  headerEl?: HTMLElement

  // STATE
  loading: boolean
}

/**
 * A simple hero for new component, just make a copy of this hero, adjust the class / tag name and add a new export to `src/riba/components/index.ts`.
 */
export class SbHeroComponent extends Component {
  /**
   * The tag name of the element, in this case `<sb-hero foobar="hello">...</sb-hero>`
   */
  public static tagName = 'sb-hero'

  public videoEl?: HTMLVideoElement

  /**
   * Everything that is assigned to the scope can be used within the hero with the Riba hero engine.
   * Examples:
   * - Methods can be called with `<div rv-on-click="show"></div>`, this will call the show method defined in the scope
   * - Properties can be used with `<div rv-click-active="visible"></div>`, this will add the class `active` if visible is `true` and remove the class `active` if visible is `false`,
   * - Heros can be used with `<div rv-hero="body"></div>`, `<div rv-html="body"></div>` or `<div rv-text="body"></div>`, this will be add the hero, html or string to the innerHTML of the element. But you can only use the Riba Hero Engine with `rv-hero`
   */
  public scope: Scope = {
    // OPTIONS
    showPosterOnVideoEnd: false,
    mobileImageSrc: '',
    desktopImageSrc: '',
    textColor: '',

    // METHODS
    replaceContent: this.replaceContent,
    onImageLoaded: this.onImageLoaded,

    // TEMPLATES

    // ELEMENTS
    preheadingEl: undefined,
    headerEl: undefined,

    // STATE
    loading: true, // Start with loading state
  }

  /**
   * Called as soon as this custom element es registered as a available custom element
   */
  constructor() {
    super()

    // Bind event listeners to `this`, so we can easily pass them to DOM event handlers and remove them again later
    this.onImageLoaded = this.onImageLoaded.bind(this)
    this.onResize = this.onResize.bind(this)
  }

  /**
   * Returns the new image source url but inherits the old `searchParams` for `width`, `height` and `crop`.
   * @param curSrc The current4 / old image source string
   * @param newSrc The new image source string
   * @returns
   */
  protected getImageSizeUrl(curSrc: string, newSrc: string) {
    const curUrl = new URL(curSrc, window.location.origin)
    const newUrl = new URL(newSrc, window.location.origin)

    const width = curUrl.searchParams.get('width')
    const height = curUrl.searchParams.get('height')
    const crop = curUrl.searchParams.get('crop')

    if (width) newUrl.searchParams.set('width', width)
    if (height) newUrl.searchParams.set('height', height)
    if (crop) newUrl.searchParams.set('crop', crop)
    return newUrl
  }

  protected async onImageLoaded(eventName: 'changed' | 'loaded') {
    if (eventName === 'loaded') {
      // Set loading to false after 500ms (css transition duration) to show the spinner
      setTimeout(() => {
        this.setLoading(false)
      }, 500)
      this.onResize()
    }
  }

  /**
   * Replaces the images sources, but keeps the sizes and cropping filters of the old images
   * @param content
   * @returns
   */
  protected replaceImageSource(content: SbHeroComponentContent) {
    this.scope.mobileImageSrc =
      content.mobileImageSrc || this.scope.mobileImageSrc
    this.scope.desktopImageSrc =
      content.desktopImageSrc || this.scope.desktopImageSrc
  }

  /**
   * Removes all background and text color css classes
   */
  protected removeColorClasses() {
    for (const cls of Array.from(this.classList)) {
      if (
        cls.startsWith('bg-') ||
        cls.startsWith('text-') ||
        cls.startsWith('rte-')
      ) {
        this.classList.remove(cls)
      }
    }
  }

  /**
   * Replaces the color background and text css classes
   * @param bg
   * @param text
   */
  protected replaceColorClasses(bg = 'primary', text?: string) {
    this.removeColorClasses()
    this.setColorClasses(bg, text)
  }

  protected setTextColorClasses(bgColor?: string, textColor?: string) {
    const textColorClasses: string[] = []
    bgColor = handleizeColor(bgColor)
    textColor = handleizeColor(textColor)

    if (textColor?.startsWith('text-')) {
      textColorClasses.push(textColor)
    } else if ((!textColor || textColor?.includes('auto')) && bgColor) {
      textColorClasses.push(...getTextColorForBackground(bgColor))
    } else {
      textColor = 'text-' + textColor
      textColorClasses.push(textColor)
    }

    this.classListAdd(textColorClasses)
    this.scope.textColor = textColorClasses.join(' ')
    return this.scope.textColor
  }

  protected setBackgroundColorClasses(bgColor?: string) {
    bgColor = handleizeColor(bgColor)

    const bgHexColor = bgColor?.startsWith('#') ? bgColor : undefined

    if (bgHexColor) {
      this.style.backgroundColor = bgHexColor
      this.scope.bgColor = bgHexColor
    } else if (bgColor?.startsWith('bg-')) {
      this.scope.bgColor = bgColor
      this.classListAdd([this.scope.bgColor])
    } else if (bgColor) {
      bgColor = 'bg-' + bgColor
      this.classListAdd([bgColor])
      this.scope.bgColor = bgColor
    } else {
      this.scope.bgColor = undefined
    }

    return this.scope.bgColor
  }

  protected classListAdd(classNames: string[]) {
    const _classNames: string[] = []
    for (let className of classNames) {
      className = className.trim()
      if (className.includes(' ')) {
        _classNames.push(...className.split(' '))
      } else {
        _classNames.push(className)
      }
    }
    this.classList.add(..._classNames)
  }

  protected setButtonColorClasses(bgColor?: string) {
    this.scope.buttonColor = getButtonVariantForBackground(bgColor)
  }

  protected setColorClasses(bgColor?: string, textColor?: string) {
    this.setTextColorClasses(bgColor, textColor)
    this.setBackgroundColorClasses(bgColor)
    this.setButtonColorClasses(bgColor)
  }

  /**
   * Replaces the heading and preheading html content
   * @param heading
   * @param preheading
   */
  protected replaceHeadHTML(heading = '', preheading = '') {
    if (this.scope.headerEl) {
      this.scope.headerEl.innerHTML = heading
    } else {
      console.error('[SbHeroComponent] No headerEl element found!')
    }

    if (this.scope.preheadingEl) {
      this.scope.preheadingEl.innerHTML = preheading
    } else {
      console.error('[SbHeroComponent] No preheadingEl element found!')
    }
  }

  /**
   * Call this method from outside or inside to replace the hero content.
   *
   * @example
   * To call this method from outside you can do it this way:
   * ```
   * const heroEl = document.getElementsByTagName('sb-hero');
   * heroEl.replaceContent({
   *   bg: 'melone',
   *   ...
   * })
   * ```
   *
   * To call this method from inside (child template html) you can do it this way:
   * ```html
   * <button rv-on-click="replaceContent | args {'bg': 'melone'}"></button>
   * ```
   *
   * You can find another example in `src/riba/components/sb-collection.component.ts`
   * @param content
   */
  public replaceContent(content: SbHeroComponentContent) {
    if (content.desktopImageSrc || content.mobileImageSrc) {
      this.replaceImageSource(content)
    }

    this.replaceColorClasses(content.bg, content.textColor)

    this.replaceHeadHTML(content.heading, content.preheading)
  }

  /**
   * Sets the loading state of the hero
   * This method is called from outside to set the loading state of the hero
   * @param isLoading
   */
  public setLoading(isLoading: boolean) {
    this.scope.loading = isLoading
    if (isLoading) {
      this.classListAdd(['loading'])
      this.classList.remove('replaced')
    } else {
      this.classList.remove('loading')
      this.classListAdd(['replaced'])
    }
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return [
      'show-poster-on-video-end',
      'poster-image-url',
      'poster-image-mobile-url',
      'desktop-image-src',
      'mobile-image-src',
      'text-color',
      'bg-color',
      'button-color',
    ]
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbHeroComponent.observedAttributes)
  }

  protected setVideoElement() {
    if (!this.videoEl) {
      this.videoEl = this.getElementsByTagName('video')?.[0] || undefined
    }
    if (!this.videoEl) return
  }

  protected _onVideoEnd(event: Event) {
    const videoEl = event.target as HTMLVideoElement
    // Show poster after video finished
    if (this.scope.showPosterOnVideoEnd && this.scope.posterImageUrl) {
      videoEl.autoplay = false
      videoEl.load()
    }
  }

  protected onVideoEnd = this._onVideoEnd.bind(this)

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  async beforeBind() {
    await super.beforeBind()
    this.replaceColorClasses(this.scope.bgColor, this.scope.textColor)
    this.setVideoElement()
    this.addEventListeners()
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    await super.afterBind()
    this.classListAdd([`${SbHeroComponent.tagName}-ready`])
  }

  protected onResize() {
    const elWidth =
      this.clientWidth ||
      this.parentElement?.clientWidth ||
      getViewportDimensions().w ||
      0

    if (this.scope.posterImageMobileUrl) {
      this.scope.posterImageMobileUrl = setShopifyImageSize(
        this.scope.posterImageMobileUrl,
        elWidth,
      )
    }
    if (this.scope.posterImageUrl) {
      this.scope.posterImageUrl = setShopifyImageSize(
        this.scope.posterImageUrl,
        elWidth,
      )
    }
  }

  protected addEventListeners() {
    window.addEventListener('resize', this.onResize)

    this.videoEl?.addEventListener('ended', this.onVideoEnd)
  }

  protected removeEventListeners() {
    window.removeEventListener('resize', this.onResize)

    this.videoEl?.removeEventListener('ended', this.onVideoEnd)
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected template() {
    return null
  }
}
